/**
 * useQuetureTest - 깨처 테스트 비즈니스 로직 훅
 */
import { useState, useCallback, useMemo } from 'react'
import type { QuetureTestApi, QuetureTestResponse, PauseAndGoItem } from './types'

export interface UseQuetureTestReturn {
  // Input state
  quetureId: string
  setQuetureId: (id: string) => void

  // Queture data
  quetureData: QuetureTestResponse | null

  // Loading/error
  isLoading: boolean
  error: string | null

  // Bundle item selection
  currentBundleItem: 'main' | 'summary'
  setCurrentBundleItem: (item: 'main' | 'summary') => void
  isSummaryMode: boolean

  // Current HLS URL based on bundle selection
  currentHlsUrl: string | null

  // PauseAndGos for current bundle item
  pauseAndGos: PauseAndGoItem[]

  // includeUnpublishedPauseAndGo toggle
  includeUnpublishedPauseAndGo: boolean
  setIncludeUnpublishedPauseAndGo: (value: boolean) => void

  // Actions
  handleFetch: (overrideId?: string, errorMessage?: string) => Promise<void>
  handleReset: () => void
}

export function useQuetureTest(api: QuetureTestApi): UseQuetureTestReturn {
  // Input state
  const [quetureId, setQuetureId] = useState('')

  // Queture state
  const [quetureData, setQuetureData] = useState<QuetureTestResponse | null>(null)

  // Loading/error state
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Bundle item selection
  const [currentBundleItem, setCurrentBundleItem] = useState<'main' | 'summary'>('main')

  // includeUnpublishedPauseAndGo toggle (default: true — 테스트 페이지에서는 미발행 포함)
  const [includeUnpublishedPauseAndGo, setIncludeUnpublishedPauseAndGo] = useState(true)

  // Convenience: is summary mode
  const isSummaryMode = currentBundleItem === 'summary'

  // Computed: current HLS URL based on bundle selection
  const currentHlsUrl = useMemo(() => {
    if (!quetureData) return null
    if (isSummaryMode && quetureData.summaryHlsUrl) {
      return quetureData.summaryHlsUrl
    }
    return quetureData.hlsUrl ?? null
  }, [quetureData, isSummaryMode])

  // Computed: all pauseAndGos (filtering by bundleItem is done in usePauseAndGo)
  const pauseAndGos = useMemo<PauseAndGoItem[]>(() => {
    return quetureData?.pauseAndGos ?? []
  }, [quetureData])

  // Fetch queture
  const handleFetch = useCallback(
    async (overrideId?: string, errorMessage?: string) => {
      const targetId = overrideId || quetureId
      if (!targetId.trim()) {
        setError(errorMessage || 'Please enter a queture ID')
        return
      }

      setIsLoading(true)
      setError(null)
      setQuetureData(null)

      if (overrideId) {
        setQuetureId(overrideId)
      }

      try {
        const data = await api.getQuetureInfo(targetId.trim(), includeUnpublishedPauseAndGo)
        setQuetureData(data)
        setCurrentBundleItem('main')
      } catch (err) {
        console.error('Failed to fetch queture:', err)
        setError(errorMessage || 'Failed to fetch queture')
      } finally {
        setIsLoading(false)
      }
    },
    [quetureId, api, includeUnpublishedPauseAndGo]
  )

  // Reset
  const handleReset = useCallback(() => {
    setQuetureData(null)
    setError(null)
    setQuetureId('')
    setCurrentBundleItem('main')
  }, [])

  return {
    // Input state
    quetureId,
    setQuetureId,

    // Queture data
    quetureData,

    // Loading/error
    isLoading,
    error,

    // Bundle item selection
    currentBundleItem,
    setCurrentBundleItem,
    isSummaryMode,

    // Computed
    currentHlsUrl,
    pauseAndGos,

    // includeUnpublishedPauseAndGo toggle
    includeUnpublishedPauseAndGo,
    setIncludeUnpublishedPauseAndGo,

    // Actions
    handleFetch,
    handleReset,
  }
}
